import { parseConfig, getLazyLoadComponentObserver, observeLazyLoadComponent } from '@utilities/theme';

/**
 * ProductListComponentBase - Base class for components that display lists of products
 *
 * Provides shared functionality for product list components including:
 * - Data fetching from Shopify/Nosto APIs
 * - State management (loading, error, data)
 * - Template management
 * - Lazy loading support
 * - Vendor switching
 * - Request cancellation
 *
 * Subclasses must implement:
 * - fetchShopifyData()
 * - fetchNostoData()
 * - renderProducts(products, vendor)
 * - getContainerElement()
 * - initTemplates()
 * - setComponentState(state)
 *
 * @abstract
 * @class
 * @extends HTMLElement
 */
export class ProductListComponentBase extends HTMLElement {
  /**
   * Creates an instance of ProductListComponentBase
   * @constructor
   */
  constructor() {
    super();

    // Configuration - can be overridden by subclasses
    this.config = {};

    // Shared state management
    this.state = {
      isLoading: false,
      hasError: false,
      data: null
    };

    // DOM element references
    this.elements = {};

    // Template references
    this.templates = {};

    // Request cancellation
    this.abortController = null;

    // Vendor change handler binding
    this.handleVendorChange = this.handleVendorChange.bind(this);
  }

  /**
   * Check if debug mode is enabled
   * @returns {boolean} Whether debug mode is enabled
   */
  allowDebug() {
    return this.classList.contains('debug');
  }

  /**
   * Check if lazy loading is enabled
   * @returns {boolean} Whether lazy loading is enabled
   */
  isLazyLoad() {
    return this.classList.contains('lazy-load');
  }

  /**
   * Check whether nostodebug URL override is enabled
   * @returns {boolean}
   */
  isNostoDebugEnabled() {
    return window.theme?.utilities?.isNostoDebugEnabled?.() === true;
  }

  /**
   * Check if this component uses a context-specific vendor from component-config
   * (independent of the global vendor and vendor:changed events)
   * @returns {boolean}
   */
  hasContextVendor() {
    if (this.isNostoDebugEnabled()) {
      return false;
    }

    return this.config?.usesContextVendor === true;
  }

  /**
   * Resolve the effective data vendor for this component
   * @returns {'shopify'|'nosto'}
   */
  getDataVendor() {
    const componentVendor = this.hasContextVendor() ? this.config?.vendor : undefined;
    return (
      window.theme?.utilities?.resolveDataVendor?.(componentVendor) ??
      window.theme?.data?.source ??
      'shopify'
    );
  }

  /**
   * Update component state
   * @param {Object} newState - New state properties to merge
   */
  setState(newState) {
    this.state = { ...this.state, ...newState };
  }

  /**
   * Parse configuration from component config script
   */
  parseConfiguration() {
    this.config = parseConfig(this, this.allowDebug());
  }

  /**
   * Initialize component when added to DOM
   */
  async connectedCallback() {
    this.setComponentState('loading');
    document.addEventListener('vendor:changed', this.handleVendorChange);

    if (this.isLazyLoad()) {
      await observeLazyLoadComponent(this, { threshold: 0 });
      if (this.allowDebug()) {
        console.log(`${this.constructor.name} is lazy-loading`, this);
      }
    } else {
      await this.init();
    }
  }

  /**
   * Cleanup when component is removed from DOM
   */
  disconnectedCallback() {
    this.abortController?.abort();
    document.removeEventListener('vendor:changed', this.handleVendorChange);

    const observer = getLazyLoadComponentObserver();
    if (observer) {
      observer.unobserve(this);
    }
  }

  /**
   * Initialize the component
   */
  async init() {
    try {
      this.parseConfiguration();
      this.initTemplates();
      await this.loadData();
    } catch (error) {
      console.error(`${this.constructor.name} initialization error:`, error);
      this.setComponentState('error');
    }
  }

  /**
   * Initialize template references
   * @abstract
   */
  initTemplates() {
    throw new Error('Subclasses must implement initTemplates()');
  }

  /**
   * Set component state for styling/CSS
   * @abstract
   * @param {string} state - State value
   */
  setComponentState(state) {
    throw new Error('Subclasses must implement setComponentState(state)');
  }

  /**
   * Load product data from appropriate vendor
   */
  async loadData() {
    if (this.state.isLoading) return;

    this.setState({ isLoading: true, hasError: false });
    this.setComponentState('loading');

    try {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const vendor = this.getDataVendor();
      let data;

      if (this.allowDebug()) {
        console.log(`${this.constructor.name}: Fetching data from ${vendor}`);
      }

      if (vendor === 'nosto') {
        data = await this.fetchNostoData();
      } else {
        data = await this.fetchShopifyData();
      }

      if (this.allowDebug()) {
        console.log(`${this.constructor.name}: Data loaded`, data);
      }

      this.setState({ data, isLoading: false });
      this.renderResults(data, vendor);

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(`${this.constructor.name}: Data loading error:`, error);
        this.setState({ hasError: true, isLoading: false });
        this.setComponentState('error');
      }
    }
  }

  /**
   * Render results based on loaded data
   * @param {Object} data - Product data
   * @param {string} vendor - Data source vendor
   */
  renderResults(data, vendor) {
    const products = data?.products || [];

    if (!products.length) {
      this.setComponentState('empty');
      const container = this.getContainerElement();
      if (container) {
        container.replaceChildren();
      }
      return;
    }

    this.setComponentState('results');
    this.renderProducts(products, vendor);
  }

  /**
   * Fetch data from Shopify API
   * @abstract
   * @returns {Promise<Object>} Product data
   */
  async fetchShopifyData() {
    throw new Error('Subclasses must implement fetchShopifyData()');
  }

  /**
   * Fetch data from Nosto API
   * @abstract
   * @returns {Promise<Object>} Product data
   */
  async fetchNostoData() {
    throw new Error('Subclasses must implement fetchNostoData()');
  }

  /**
   * Render product cards
   * @abstract
   * @param {Array} products - Product data array
   * @param {string} vendor - Data source vendor
   */
  renderProducts(products, vendor) {
    throw new Error('Subclasses must implement renderProducts(products, vendor)');
  }

  /**
   * Get the container element for product cards
   * @abstract
   * @returns {HTMLElement} Container element
   */
  getContainerElement() {
    throw new Error('Subclasses must implement getContainerElement()');
  }

  /**
   * Handle vendor change events
   * @param {CustomEvent} event - Vendor change event
   */
  async handleVendorChange(event) {
    if (this.hasContextVendor()) {
      return;
    }

    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: handleVendorChange`, event);
    }

    const { vendor } = event.detail || {};
    if (this.allowDebug()) {
      console.log(`${this.constructor.name}: vendor changed to ${vendor}, reloading data...`);
    }

    // Re-initialize templates in case DOM changed
    this.initTemplates();

    await this.loadData();
  }
}